import styled from "styled-components";
import {
  ArrowUturnLeftIcon,
  CreditCardIcon,
  PresentationChartLineIcon,
} from "@heroicons/react/24/outline";
import { Label } from "@sablier/v2-components/molecules";
import { StreamCategory } from "@sablier/v2-constants";
import { useT } from "@sablier/v2-locales";
import { _ } from "@sablier/v2-mixins";
import { Card } from "~/client/components/molecules";
import { useStreamCurrent, useToken } from "~/client/hooks";
import AmountItem from "./AmountItem";
import TimelineItem from "./TimelineItem";

const WrapperPartial = styled(Card)`
  & > * {
    width: ${(props) => props.theme.sizes.streamDetailsWidth};
    border: 2px solid ${(props) => props.theme.colors.border};
    background-color: ${(props) => props.theme.colors.modalDetailsBackground};
  }
`;

const List = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
    width: 100%;
    padding-top: calc(${(props) => props.theme.sizes.edge} * 3 / 2);
    padding-bottom: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: calc(${(props) => props.theme.sizes.edge} * 1);
  width: 100%;
`;

const Timelines = styled.div`
  ${(props) => props.theme.styles.column}
  & {
    row-gap: calc(${(props) => props.theme.sizes.edge} * 3 / 4);
    width: 100%;
  }
`;

const Divider = styled.div`
  width: 100%;
  height: 2px;
  background-color: ${(props) => props.theme.colors.border};
`;

const Wrapper = styled(WrapperPartial)`
  ${(props) => props.theme.medias.maxLG} {
    width: 100%;
    & > * {
      width: 100%;
    }
    ${List} {
      row-gap: calc(${(props) => props.theme.sizes.edge} * 1);
      padding-top: calc(${(props) => props.theme.sizes.edge} * 1);
      padding-bottom: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
    }
    ${Grid} {
      grid-template-columns: 1fr;
      gap: calc(${(props) => props.theme.sizes.edge} * 1 / 2);
      br {
        display: none;
      }
    }
  }
`;

function Accounting() {
  const { stream, preview } = useStreamCurrent();
  const token = useToken({ token: stream?.token });
  const { t } = useT();

  if (_.isNil(stream)) {
    return false;
  }

  return (
    <Wrapper title={t("words.accounting")}>
      <List>
        <Label value={t("words.amounts")} />
        <Grid>
          <AmountItem
            amount={stream.depositAmount.humanized.toString()}
            label={{ icon: CreditCardIcon, value: t("structs.depositAmount") }}
            token={token}
          />
          {stream.category === StreamCategory.LOCKUP_LINEAR && stream.cliff ? (
            <AmountItem
              amount={stream.cliffAmount.humanized.toString()}
              label={{
                icon: PresentationChartLineIcon,
                value: t("structs.cliffAmount"),
              }}
              percentage={stream.cliffPercentage.toString()}
              token={token}
            />
          ) : (
            false
          )}

          {stream.isCanceled ? (
            <AmountItem
              amount={stream.returnableAmount.humanized.toString()}
              label={{
                color: "red",
                icon: ArrowUturnLeftIcon,
                value: t("structs.refundedAmount"),
              }}
              indicator={"red"}
              percentage={stream.returnableAmountPercentage.toString()}
              token={token}
            />
          ) : (
            false
          )}

          <AmountItem
            amount={stream.streamedAmount.humanized.toString()}
            label={{
              icon: CreditCardIcon,
              value: t("structs.fromDeposit", { action: "Streamed" }),
            }}
            indicator={"secondaryDesaturated"}
            percentage={stream.streamedAmountPercentage.toString()}
            token={token}
          />
          <AmountItem
            amount={stream.withdrawnAmount.humanized.toString()}
            label={{
              icon: CreditCardIcon,
              value: t("structs.fromDeposit", { action: "Withdrawn" }),
            }}
            indicator={"primaryMiddle"}
            percentage={stream.withdrawnAmountPercentage.toString()}
            token={token}
          />
        </Grid>
        <Divider />
        <Label value={t("words.timeline")} />
        <Timelines>
          <TimelineItem
            duration={{
              purpose: "time",
              prepared: preview.timeSinceEnd,
            }}
            prefix={_.capitalize(
              `${t("words.the")} ${preview.timeSinceEndPrefix}`,
            )}
          />
          <TimelineItem
            duration={{ purpose: "date", milliseconds: stream.startTime }}
            prefix={t("structs.itStartedOn")}
          />
          {!stream.isCanceled && (
            <TimelineItem
              duration={{ purpose: "date", milliseconds: stream.endTime }}
              prefix={t("structs.willEnd")}
            />
          )}
          <TimelineItem
            duration={{ purpose: "time", milliseconds: stream.duration }}
            prefix={t("structs.itsTotalDuration")}
          />
          {stream.category === StreamCategory.LOCKUP_LINEAR && stream.cliff && (
            <>
              <Divider />
              <TimelineItem
                duration={{
                  purpose: "time",
                  prepared: preview.timeSinceCliff,
                }}
                prefix={_.capitalize(
                  `${t("words.the")} ${preview.timeSinceCliffPrefix}`,
                )}
              />
              <TimelineItem
                duration={{
                  purpose: "date",
                  milliseconds: stream.cliffTime,
                }}
                prefix={t("structs.theCliffEndsOn")}
              />
              <TimelineItem
                duration={{
                  purpose: "time",
                  milliseconds: stream.cliffDuration,
                }}
                prefix={t("structs.itsDuration")}
              />
            </>
          )}
        </Timelines>
      </List>
    </Wrapper>
  );
}

export default Accounting;
